import { useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Loader2, Camera, Briefcase } from "lucide-react";

const PAYMENTS = [
  { v: "company_card", l: "Tarjeta de empresa" },
  { v: "cash", l: "Efectivo" },
  { v: "employee", l: "Pagado por el empleado" },
  { v: "transfer", l: "Transferencia" },
];
const VAT_RATES = ["21", "10", "4", "0"];

const blank = () => ({
  date: new Date().toISOString().slice(0, 10),
  vendor: "", description: "", amount: "", vat_rate: "21",
  category_id: "", payment_method: "company_card",
  hasProject: false, project_id: "", photo_path: null, ai_used: false, user_id: "",
});

export default function ExpenseForm({ projects = [], categories = [], employees = null, onSaved }) {
  const [form, setForm] = useState(blank());
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef();

  const scan = async (file) => {
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setScanning(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/expenses/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ex = data.extracted || {};
      setForm((f) => ({
        ...f,
        photo_path: data.photo_path,
        ai_used: data.ai_used,
        vendor: ex.vendor || f.vendor,
        description: ex.description || f.description,
        amount: ex.amount != null ? String(ex.amount) : f.amount,
        vat_rate: ex.vat_rate != null ? String(Math.round(ex.vat_rate)) : f.vat_rate,
        category_id: data.suggested_category_id || f.category_id,
        date: ex.date || f.date,
      }));
      if (data.ai_used) toast.success("Ticket leído con IA · revisa los datos");
      else toast.message("Foto guardada. Rellena los datos manualmente.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setScanning(false); }
  };

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Indica el importe");
    if (!form.date) return toast.error("Indica la fecha");
    if (form.hasProject && !form.project_id) return toast.error("Selecciona el trabajo/proyecto");
    if (employees && !form.user_id) return toast.error("Selecciona el empleado");
    setSaving(true);
    try {
      await api.post("/expenses", {
        date: form.date, vendor: form.vendor, description: form.description,
        amount: Number(form.amount), vat_rate: Number(form.vat_rate),
        category_id: form.category_id || null,
        project_id: form.hasProject ? form.project_id : null,
        payment_method: form.payment_method, photo_path: form.photo_path,
        ai_used: form.ai_used, user_id: employees ? form.user_id : undefined,
      });
      toast.success("Gasto registrado");
      setForm(blank()); setPreview(null);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {/* Escaneo IA */}
      <div className="rounded-xl border-2 border-dashed border-sky-200 bg-sky-50/50 p-4">
        <input type="file" accept="image/*" hidden ref={fileRef} onChange={(e) => scan(e.target.files[0])} data-testid="expense-photo-input" />
        <div className="flex items-center gap-3">
          {preview ? (
            <img src={preview} alt="ticket" className="h-16 w-16 rounded-lg object-cover border border-slate-200" />
          ) : (
            <div className="h-16 w-16 rounded-lg bg-white border border-slate-200 flex items-center justify-center"><Camera className="h-6 w-6 text-slate-300" /></div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><Sparkles className="h-4 w-4 text-sky-500" /> Escanear ticket con IA</div>
            <p className="text-xs text-slate-500 mt-0.5">Sube la foto y rellenamos los datos por ti. (Opcional)</p>
          </div>
          <Button type="button" variant="outline" size="sm" disabled={scanning} onClick={() => fileRef.current?.click()} data-testid="expense-scan-btn" className="border-sky-300 text-sky-700">
            {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : "Subir foto"}
          </Button>
        </div>
      </div>

      {employees && (
        <div>
          <Label>Empleado *</Label>
          <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
            <SelectTrigger className="mt-1" data-testid="expense-employee"><SelectValue placeholder="Selecciona empleado" /></SelectTrigger>
            <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div><Label>Fecha *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" data-testid="expense-date" /></div>
        <div><Label>Importe total (€) *</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1" data-testid="expense-amount" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Proveedor</Label><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="mt-1" data-testid="expense-vendor" /></div>
        <div>
          <Label>IVA</Label>
          <Select value={form.vat_rate} onValueChange={(v) => setForm({ ...form, vat_rate: v })}>
            <SelectTrigger className="mt-1" data-testid="expense-vat"><SelectValue /></SelectTrigger>
            <SelectContent>{VAT_RATES.map((r) => <SelectItem key={r} value={r}>{r}%</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Descripción</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" rows={2} data-testid="expense-description" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Categoría</Label>
          <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
            <SelectTrigger className="mt-1" data-testid="expense-category"><SelectValue placeholder="Sin categoría" /></SelectTrigger>
            <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Forma de pago</Label>
          <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
            <SelectTrigger className="mt-1" data-testid="expense-payment"><SelectValue /></SelectTrigger>
            <SelectContent>{PAYMENTS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Repercusión a trabajo fuera de oficina */}
      <div className="rounded-xl border border-slate-200 p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700"><Briefcase className="h-4 w-4 text-slate-400" /> ¿Es por un trabajo fuera de oficina?</div>
          <Switch checked={form.hasProject} onCheckedChange={(v) => setForm({ ...form, hasProject: v })} data-testid="expense-has-project" />
        </div>
        {form.hasProject && (
          <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
            <SelectTrigger data-testid="expense-project"><SelectValue placeholder="Selecciona el trabajo/proyecto" /></SelectTrigger>
            <SelectContent>{projects.filter((p) => p.status !== "closed").map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.client ? ` · ${p.client}` : ""}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      <Button onClick={submit} disabled={saving} className="brand-btn w-full" data-testid="expense-submit-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registrar gasto"}
      </Button>
    </div>
  );
}
